import * as THREE from "three";
import { genTex, type TexData } from "./textures.gen";

const cache = new Map<string, BodyTextures>();

export interface BodyTextures {
  map: THREE.DataTexture;
  roughness?: THREE.DataTexture;
  emissive?: THREE.DataTexture;
  clouds?: THREE.DataTexture;
}

/** 行优先、自上而下 → 翻转为 WebGL 的自下而上，并设置通用采样参数。 */
function rgba(w: number, h: number, src: (i: number) => [number, number, number, number], srgb: boolean): THREE.DataTexture {
  const out = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const [r, g, b, a] = src((h - 1 - y) * w + x);
      const o = (y * w + x) * 4;
      out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
    }
  }
  const t = new THREE.DataTexture(out, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = 4;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

export function bodyTextures(id: string): BodyTextures {
  const hit = cache.get(id);
  if (hit) return hit;
  const T: TexData = genTex({ id });
  const { w, h, d, aux } = T;
  const map = rgba(w, h, (i) => [d[i * 4]!, d[i * 4 + 1]!, d[i * 4 + 2]!, 255], true);
  const out: BodyTextures = { map };
  if (id === "earth" && aux) {
    // 海洋（d.alpha=255）光滑，陆地粗糙
    out.roughness = rgba(w, h, (i) => [0, d[i * 4 + 3]! > 128 ? 70 : 215, 0, 255], false);
    out.clouds = rgba(w, h, (i) => [255, 255, 255, aux[i * 4]!], true);
    out.emissive = rgba(w, h, (i) => [aux[i * 4 + 1]!, Math.round(aux[i * 4 + 1]! * 0.8), Math.round(aux[i * 4 + 1]! * 0.45), 255], true);
  }
  cache.set(id, out);
  return out;
}

/** 太阳表面：颗粒翻滚的橙黄色（自发光，不参与光照）。 */
export function sunTexture(): THREE.CanvasTexture {
  const W = 512, H = 256, c = document.createElement("canvas");
  c.width = W; c.height = H;
  const g = c.getContext("2d")!;
  const base = g.createLinearGradient(0, 0, 0, H);
  base.addColorStop(0, "#ffb347"); base.addColorStop(0.5, "#ffd27a"); base.addColorStop(1, "#ffb347");
  g.fillStyle = base; g.fillRect(0, 0, W, H);
  let s = 12345; const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < 900; i++) {
    const x = rnd() * W, y = rnd() * H, r = 4 + rnd() * 16;
    const warm = rnd() < 0.55;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, warm ? "rgba(255,140,50,.38)" : "rgba(255,245,200,.35)");
    gr.addColorStop(1, "rgba(255,200,100,0)");
    g.fillStyle = gr;
    for (const dx of [0, -W, W]) { g.beginPath(); g.arc(x + dx, y, r, 0, Math.PI * 2); g.fill(); }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; t.wrapS = THREE.RepeatWrapping; t.anisotropy = 4;
  return t;
}

/** 土星环：径向条带（从内缘到外缘），带透明度。 */
export function ringTexture(): THREE.CanvasTexture {
  const W = 512, c = document.createElement("canvas");
  c.width = W; c.height = 2;
  const g = c.getContext("2d")!;
  const bands: [number, number, string][] = [
    [0.0, 0.12, "rgba(160,140,105,.35)"], [0.12, 0.38, "rgba(196,170,120,.62)"], [0.38, 0.42, "rgba(60,50,40,.12)"],
    [0.42, 0.72, "rgba(236,214,166,.82)"], [0.72, 0.76, "rgba(60,50,40,.1)"], [0.76, 0.9, "rgba(176,152,110,.55)"], [0.9, 1.0, "rgba(150,130,100,.25)"],
  ];
  g.clearRect(0, 0, W, 2);
  for (const [a, b, col] of bands) { g.fillStyle = col; g.fillRect(a * W, 0, (b - a) * W, 2); }
  // 细纹
  for (let i = 0; i < 160; i++) { g.fillStyle = `rgba(${i % 2 ? "255,245,225" : "90,75,55"},${0.05 + (i % 5) * 0.015})`; g.fillRect(Math.floor((i / 160) * W), 0, 1 + (i % 3), 2); }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; t.wrapS = THREE.ClampToEdgeWrapping;
  return t;
}

/** 银河背景：按真实银道面朝向（银北极在黄道坐标 λ=180°, β=+29.8°）生成等距柱状贴图。 */
export function milkyWayTexture(): THREE.CanvasTexture {
  const W = 1024, H = 512, c = document.createElement("canvas");
  c.width = W; c.height = H;
  const g = c.getContext("2d")!, img = g.createImageData(W, H), D = img.data;
  const D2R = Math.PI / 180;
  const ngp = [Math.cos(29.8 * D2R) * Math.cos(180 * D2R), Math.cos(29.8 * D2R) * Math.sin(180 * D2R), Math.sin(29.8 * D2R)];
  const gc = [Math.cos(-5.54 * D2R) * Math.cos(266.84 * D2R), Math.cos(-5.54 * D2R) * Math.sin(266.84 * D2R), Math.sin(-5.54 * D2R)];
  const hash = (i: number, j: number, k: number) => { let h = Math.imul(i, 374761393) ^ Math.imul(j, 668265263) ^ Math.imul(k, 1597334677); h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967295; };
  const vn = (x: number, y: number, z: number) => {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z), xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf), l = (a: number, b: number, t: number) => a + (b - a) * t;
    return l(l(l(hash(xi, yi, zi), hash(xi + 1, yi, zi), u), l(hash(xi, yi + 1, zi), hash(xi + 1, yi + 1, zi), u), v), l(l(hash(xi, yi, zi + 1), hash(xi + 1, yi, zi + 1), u), l(hash(xi, yi + 1, zi + 1), hash(xi + 1, yi + 1, zi + 1), u), v), w);
  };
  const fbm = (x: number, y: number, z: number, o: number) => { let a = 0.5, f = 1, s = 0, n = 0; for (let i = 0; i < o; i++) { s += a * vn(x * f, y * f, z * f); n += a; a *= 0.5; f *= 2.03; } return s / n; };
  for (let j = 0; j < H; j++) {
    const lat = Math.PI / 2 - ((j + 0.5) / H) * Math.PI, cl = Math.cos(lat);
    for (let i = 0; i < W; i++) {
      const lon = ((i + 0.5) / W) * Math.PI * 2 - Math.PI;
      const x = cl * Math.cos(lon), y = cl * Math.sin(lon), z = Math.sin(lat);
      const b = Math.asin(x * ngp[0]! + y * ngp[1]! + z * ngp[2]!);
      const band = Math.exp(-0.5 * (b / 0.2) ** 2);
      const core = Math.pow(Math.max(0, x * gc[0]! + y * gc[1]! + z * gc[2]!), 5);
      const n = fbm(x * 11, y * 11, z * 11, 5);
      const dustN = Math.min(1, Math.max(0, (fbm(x * 14 + 3, y * 14, z * 14, 4) - 0.5) / 0.22));
      const dust = 1 - 0.5 * dustN * dustN * (3 - 2 * dustN) * Math.exp(-0.5 * (b / 0.1) ** 2);
      const I = Math.min(1, (band * (0.14 + 0.26 * n) + core * 0.3) * dust);
      const o = (j * W + i) * 4;
      D[o] = 150 * I + 70 * core; D[o + 1] = 150 * I + 35 * core; D[o + 2] = 175 * I; D[o + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; t.wrapS = THREE.RepeatWrapping;
  return t;
}

let soft: THREE.CanvasTexture | null = null;
/** 柔边圆点（粒子、彗尾用）：中心亮、边缘淡出，避免方形像素点。 */
export function softPointTexture(): THREE.CanvasTexture {
  if (soft) return soft;
  const c = document.createElement("canvas"); c.width = c.height = 64;
  const g = c.getContext("2d")!, gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, "rgba(255,255,255,1)"); gr.addColorStop(0.35, "rgba(255,255,255,.55)"); gr.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  soft = new THREE.CanvasTexture(c); soft.colorSpace = THREE.SRGBColorSpace;
  return soft;
}
